import { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { HouseContext } from "../../../context/HouseContext";
import { AlquileresContext } from "../../../context/AlquileresContext";
import { useAuthContext } from "../../../context/authContext";
import { getStatusClasses } from "../../../utils/propertyStatusColors";
import { formatMonto, diasHastaFecha } from "../../../utils/rentalCalculations";
import {
  FaBuilding,
  FaFileContract,
  FaDollarSign,
  FaPlusCircle,
  FaArrowRight,
  FaExclamationTriangle,
  FaBell,
  FaClipboardList,
  FaTag,
} from "react-icons/fa";

const StatCard = ({ count, label, icon: Icon, gradient }) => (
  <div className={`rounded-xl p-5 text-white ${gradient} shadow-md hover:shadow-lg transition-shadow`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-3xl font-bold">{count}</p>
        <p className="text-sm opacity-90 mt-1">{label}</p>
      </div>
      <Icon className="w-10 h-10 opacity-30" />
    </div>
  </div>
);

function DashboardHome() {
  const { allHouses } = useContext(HouseContext);
  const { contratosActivos, contratosPorVencer, contratosConAumentoProximo } =
    useContext(AlquileresContext);
  const { user } = useAuthContext();

  const [stats, setStats] = useState({
    total: 0,
    alquiler: 0,
    venta: 0,
    alquilado: 0,
    vendido: 0,
    destacadas: 0,
  });

  useEffect(() => {
    if (!allHouses) return;
    const s = {
      total: allHouses.length,
      alquiler: 0,
      vfitzal: 0,
      alquilado: 0,
      vendido: 0,
      destacadas: 0,
    };
    allHouses.forEach((h) => {
      if (h.state === "Alquiler") s.alquiler++;
      if (h.state === "Venta") s.venta++;
      if (h.state === "Alquilado") s.alquilado++;
      if (h.state === "Vendido") s.vendido++;
      if (h.highlights) s.destacadas++;
    });
    setStats(s);
  }, [allHouses]);

  const ingresosARS = contratosActivos
    .filter((c) => c.moneda === "ARS")
    .reduce((sum, c) => sum + (Number(c.comisionMonto) || 0), 0);
  const ingresosUSD = contratosActivos
    .filter((c) => c.moneda === "USD")
    .reduce((sum, c) => sum + (Number(c.comisionMonto) || 0), 0);

  const recentProperties = allHouses ? allHouses.slice(0, 5) : [];

  const today = new Date();
  const dateStr = today.toLocaleDateString("es-AR", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="px-4 md:px-6 py-4 space-y-6 min-h-screen">
      {/* Welcome Banner */}
      <section className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">
              Bienvenido{user?.displayName ? `, ${user.displayName}` : ""}
            </h1>
            <p className="text-sm opacity-80 mt-1 capitalize">{dateStr}</p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/dashboard/propiedades/agregar"
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors backdrop-blur-sm"
            >
              <FaPlusCircle className="w-4 h-4" /> Agregar Propiedad
            </Link>
            <Link
              to="/dashboard/alquileres/contratos/nuevo"
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors backdrop-blur-sm"
            >
              <FaFileContract className="w-4 h-4" /> Nuevo Contrato
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            count={stats.total}
            label="Total Propiedades"
            icon={FaBuilding}
            gradient="bg-gradient-to-br from-blue-500 to-blue-600"
          />
          <StatCard
            count={contratosActivos.length}
            label="Contratos Activos"
            icon={FaFileContract}
            gradient="bg-gradient-to-br from-emerald-500 to-emerald-600"
          />
          <StatCard
            count={
              ingresosARS > 0 || ingresosUSD > 0
                ? ingresosARS > 0 && ingresosUSD > 0
                  ? `${formatMonto(ingresosARS)}`
                  : ingresosUSD > 0
                  ? formatMonto(ingresosUSD, "USD")
                  : formatMonto(ingresosARS)
                : "0"
            }
            label={
              ingresosUSD > 0 && ingresosARS > 0
                ? `+ ${formatMonto(ingresosUSD, "USD")}`
                : "Comisiones Mensuales"
            }
            icon={FaDollarSign}
            gradient="bg-gradient-to-br from-amber-500 to-amber-600"
          />
          <StatCard
            count={stats.alquiler + stats.venta}
            label="Disponibles"
            icon={FaTag}
            gradient="bg-gradient-to-br from-violet-500 to-violet-600"
          />
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Alertas */}
        <div className="lg:col-span-2 space-y-4">
          {/* Contratos por vencer */}
          {contratosPorVencer.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <FaExclamationTriangle className="text-amber-500" />
                <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                  Contratos por Vencer
                </h2>
                <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  {contratosPorVencer.length}
                </span>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-48 overflow-y-auto">
                {contratosPorVencer.map((c) => (
                  <Link
                    key={c.idFirebase}
                    to={`/dashboard/alquileres/contratos/editar/${c.idFirebase}`}
                    className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        {c.locatarioNombre} - {c.propiedad}
                      </p>
                      <p className="text-xs text-gray-400">
                        Locador: {c.locadorNombre}
                      </p>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      {diasHastaFecha(c.fechaVencimiento)}d
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Aumentos proximos */}
          {contratosConAumentoProximo.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <FaBell className="text-blue-500" />
                <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                  Aumentos Proximos
                </h2>
                <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {contratosConAumentoProximo.length}
                </span>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-48 overflow-y-auto">
                {contratosConAumentoProximo.map((c) => (
                  <Link
                    key={c.idFirebase}
                    to="/dashboard/alquileres/aumentos"
                    className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        {c.locatarioNombre} - {c.propiedad}
                      </p>
                      <p className="text-xs text-gray-400">
                        Actual: {formatMonto(c.montoActual, c.moneda)}
                      </p>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      {diasHastaFecha(c.aumento?.proximoAumento)}d
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Recent Properties */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                Propiedades Recientes
              </h2>
              <Link
                to="/dashboard/propiedades"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                Ver todas <FaArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {recentProperties.length === 0 ? (
                <p className="px-5 py-6 text-gray-400 text-center">
                  No hay propiedades cargadas
                </p>
              ) : (
                recentProperties.map((house) => (
                  <Link
                    key={house.id}
                    to={`/dashboard/propiedades/editar/${house.id}`}
                    className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <img
                      src={house.images?.[0]}
                      alt={house.name}
                      className="w-14 h-10 object-cover rounded-md flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate capitalize">
                        {house.name}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {house.country} - {house.address}
                      </p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusClasses(
                        house.state
                      )}`}
                    >
                      {house.state}
                    </span>
                    {house.price && (
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        ${Number(house.price).toLocaleString("es-AR")}
                      </span>
                    )}
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right column - Quick Actions */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Acciones Rapidas
            </h2>
            <div className="space-y-2">
              <Link
                to="/dashboard/alquileres/contratos/nuevo"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <FaFileContract className="text-blue-500 w-4 h-4" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nuevo Contrato
                </span>
              </Link>
              <Link
                to="/dashboard/propiedades/agregar"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                  <FaPlusCircle className="text-emerald-500 w-4 h-4" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Agregar Propiedad
                </span>
              </Link>
              <Link
                to="/dashboard/alquileres/liquidacion"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                  <FaDollarSign className="text-amber-500 w-4 h-4" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Liquidacion
                </span>
              </Link>
              <Link
                to="/dashboard/alquileres/contratos"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-violet-50 dark:bg-violet-900/20 flex items-center justify-center">
                  <FaClipboardList className="text-violet-500 w-4 h-4" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ver Contratos
                </span>
              </Link>
            </div>
          </div>

          {/* Property stats mini */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">
              Propiedades
            </h2>
            <div className="space-y-2">
              {[
                { label: "En Alquiler", count: stats.alquiler, color: "text-green-500" },
                { label: "En Venta", count: stats.venta, color: "text-blue-500" },
                { label: "Alquilados", count: stats.alquilado, color: "text-amber-500" },
                { label: "Vendidos", count: stats.vendido, color: "text-gray-400" },
                { label: "Destacadas", count: stats.destacadas, color: "text-yellow-500" },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{item.label}</span>
                  <span className={`text-sm font-bold ${item.color}`}>{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardHome;
